/**
 * Форма записи (Шаг 6): услуга → офис → день и время → данные → SMS-код → готово.
 * Экран перерисовывается целиком при смене шага; ввод в поля пишется в состояние без
 * перерисовки, чтобы не терять фокус.
 */
import type {
  ConfirmedAppointment,
  HoldResponse,
  PublicAvailability,
  PublicConfig,
  PublicLocation,
  PublicService,
  VerificationResponse,
} from '@dentbook/shared';
import {
  DEFAULT_COUNTRY,
  DIAL_CODES,
  NUMBER_FORMATS,
  type CountryCode,
} from '@dentbook/shared/countries';
import type { Locale } from '@dentbook/shared/domain';
import { maskNational, toE164In } from '@dentbook/shared/phone';
import { groupServices } from '@dentbook/shared/service-search';
import { createApi, WidgetApiError } from './api';
import { renderCaptcha, type Captcha } from './captcha';
import { addDays, formatDateOf, formatDay, formatTime, todayIn } from './dates';
import { translator, type MessageKey } from './i18n';
import { createPopup } from './popup';
import { css } from './styles';

export interface WidgetOptions {
  key: string;
  /** Адрес API, по умолчанию — origin скрипта. */
  apiBase: string;
  /** Язык формы; по умолчанию — язык из настроек клиники. */
  locale?: Locale | undefined;
  /** Всплывающее окно вместо формы на странице: появляется по open(). */
  popup?: boolean | undefined;
}

/** Форма на сайте. open() открывает всплывающее окно; форме на странице он не нужен. */
export interface Widget {
  open(): void;
}

type Step = 'loading' | 'unavailable' | 'service' | 'office' | 'time' | 'details' | 'code' | 'done';

/** Сколько дней показывать за раз. */
const WINDOW_DAYS = 14;

type Child = Node | string | false | null | undefined;

function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: Record<string, unknown> = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  for (const [name, value] of Object.entries(props)) {
    if (value === undefined || value === null || value === false) continue;
    if (name.startsWith('on') && typeof value === 'function') {
      el.addEventListener(name.slice(2).toLowerCase(), value as EventListener);
    } else if (name === 'class') {
      el.className = String(value);
    } else if (name in el) {
      Reflect.set(el, name, value);
    } else {
      el.setAttribute(name, value === true ? '' : String(value));
    }
  }
  for (const child of children) {
    if (child !== false && child !== null && child !== undefined) el.append(child);
  }
  return el;
}

/** Ключ для поиска: без регистра и диакритики — «Türkiye» находится и по «turkiye». */
const fold = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const ERROR_TEXT: Partial<Record<string, MessageKey>> = {
  slot_taken: 'error.slot_taken',
  hold_expired: 'error.hold_expired',
  verification_failed: 'error.verification_failed',
  verification_required: 'error.verification_required',
  rate_limited: 'error.rate_limited',
  invalid_key: 'error.unavailable',
  origin_not_allowed: 'error.unavailable',
};

/** С какого числа услуг в списке появляется поиск: в коротком списке он только мешает. */
const SEARCH_FROM = 5;

export function mountWidget(host: HTMLElement, options: WidgetOptions): Widget {
  const shadow = host.shadowRoot ?? host.attachShadow({ mode: 'open' });
  const view = h('div', { class: 'db', part: 'form' });
  const popup = options.popup ? createPopup(view) : undefined;
  // Куда рисуются экраны. В окне над ними крестик — он не перерисовывается с экранами,
  // и фокус, который ставит на него showModal(), не теряется
  const screens = popup ? view.appendChild(h('div')) : view;
  shadow.replaceChildren(h('style', {}, css), popup?.dialog ?? view);
  const api = createApi(options.apiBase, options.key);

  let locale: Locale = options.locale ?? 'en';
  let t = translator(locale);
  popup?.label(t('popup.title'), t('close'));

  const s = {
    step: 'loading' as Step,
    config: undefined as PublicConfig | undefined,
    services: [] as PublicService[],
    service: undefined as PublicService | undefined,
    location: undefined as PublicLocation | undefined,
    from: '',
    days: [] as PublicAvailability['days'],
    date: undefined as string | undefined,
    alternatives: [] as string[],
    hold: undefined as HoldResponse | undefined,
    form: { name: '', phone: '', email: '', notes: '' },
    /** Страна телефона: код в поле, номер клиента в form.phone — национальная часть. */
    country: DEFAULT_COUNTRY.en as CountryCode,
    phone: '',
    verificationId: '',
    code: '',
    appointment: undefined as ConfirmedAppointment | undefined,
    cancelled: false,
    error: undefined as MessageKey | undefined,
    busy: false,
  };

  // Капча живёт, пока держится слот: перерисовка шага не сбрасывает пройденную проверку
  let captcha: Captcha | undefined;
  let captchaBox: HTMLElement | undefined;
  let countdown: ReturnType<typeof setInterval> | undefined;

  const zone = () => s.location?.time_zone ?? 'UTC';

  function show(step: Step, patch: Partial<typeof s> = {}) {
    Object.assign(s, { error: undefined, ...patch, step });
    render();
  }

  function fail(error: unknown) {
    // 503 — у клиники не настроены SMS: записаться онлайн сейчас нельзя
    const key: MessageKey =
      error instanceof WidgetApiError
        ? error.status === 503
          ? 'error.unavailable'
          : (ERROR_TEXT[error.code] ?? 'error.generic')
        : 'error.generic';
    s.busy = false;
    s.error = key;
    render();
  }

  /** Запрос с блокировкой кнопок на время ожидания. */
  async function run(action: () => Promise<void>) {
    if (s.busy) return;
    s.busy = true;
    render();
    try {
      await action();
    } catch (error) {
      fail(error);
      return;
    }
    s.busy = false;
    render();
  }

  // --- действия ---

  async function start() {
    try {
      const [config, services] = await Promise.all([
        api.get<PublicConfig>('/config'),
        api.get<PublicService[]>('/services'),
      ]);
      if (!options.locale) {
        locale = config.clinic.locale;
        t = translator(locale);
        popup?.label(t('popup.title'), t('close'));
      }
      s.country = DEFAULT_COUNTRY[locale];
      const primary = config.theme.primary_color;
      if (typeof primary === 'string' && /^#[0-9a-f]{3,8}$/i.test(primary)) {
        view.style.setProperty('--db-primary', primary);
      }
      s.config = config;
      s.services = services;
      show(services.length > 0 && config.locations.length > 0 ? 'service' : 'unavailable');
    } catch {
      show('unavailable');
    }
  }

  function chooseService(service: PublicService) {
    s.service = service;
    const offices = s.config!.locations;
    if (offices.length === 1) void chooseOffice(offices[0]!);
    else show('office');
  }

  function chooseOffice(location: PublicLocation) {
    s.location = location;
    return loadDays(todayIn(location.time_zone));
  }

  async function loadDays(from: string, keepDate = false) {
    const to = addDays(from, WINDOW_DAYS - 1);
    await run(async () => {
      const result = await api.get<PublicAvailability>(
        `/availability?service_id=${s.service!.id}&location_id=${s.location!.id}&from=${from}&to=${to}`,
      );
      const firstFree = result.days.find((d) => d.slots.length > 0)?.date;
      Object.assign(s, {
        from,
        days: result.days,
        date: keepDate && s.date ? s.date : firstFree,
        step: 'time',
      });
    });
  }

  function stopCountdown() {
    if (countdown !== undefined) clearInterval(countdown);
    countdown = undefined;
  }

  function startCountdown(label: HTMLElement) {
    stopCountdown();
    const tick = () => {
      const left = Math.max(0, Date.parse(s.hold!.expires_at) - Date.now());
      const min = Math.floor(left / 60_000);
      const sec = Math.floor((left % 60_000) / 1000);
      label.textContent = t('details.reserved', { time: `${min}:${String(sec).padStart(2, '0')}` });
      if (left === 0) void backToTime('error.hold_expired');
    };
    tick();
    countdown = setInterval(tick, 1000);
  }

  async function holdSlot(startAt: string) {
    s.alternatives = [];
    await run(async () => {
      try {
        s.hold = await api.post<HoldResponse>('/holds', {
          service_id: s.service!.id,
          location_id: s.location!.id,
          start_at: startAt,
        });
        s.step = 'details';
      } catch (error) {
        if (error instanceof WidgetApiError && error.code === 'slot_taken') {
          s.alternatives = (error.body?.alternatives as string[] | undefined) ?? [];
          s.days = s.days.map((d) => ({ ...d, slots: d.slots.filter((x) => x !== startAt) }));
        }
        throw error;
      }
    });
  }

  function dropCaptcha() {
    captcha = undefined;
    captchaBox = undefined;
  }

  function releaseHold() {
    stopCountdown();
    dropCaptcha();
    if (s.hold) void api.del(`/holds/${s.hold.hold_id}`).catch(() => undefined);
    s.hold = undefined;
  }

  /**
   * Холда больше нет (клиент вернулся, время вышло или сервер ответил hold_expired):
   * сначала уйти с шагов, которым нужен холд, потом перечитать свободное время.
   */
  async function backToTime(error?: MessageKey) {
    releaseHold();
    s.step = 'time';
    s.busy = false;
    await loadDays(s.from, true);
    if (error) {
      s.error = error;
      render();
    }
  }

  async function sendCode() {
    const phone = toE164In(DIAL_CODES[s.country], s.form.phone);
    if (!s.form.name.trim()) return show('details', { error: 'error.name' });
    if (!phone) return show('details', { error: 'error.phone' });
    const captchaToken = captcha?.token();
    if (captcha && !captchaToken) return show('details', { error: 'error.captcha' });
    await run(async () => {
      try {
        const verification = await api.post<VerificationResponse>('/verifications', {
          phone,
          locale,
          ...(captchaToken ? { captcha_token: captchaToken } : {}),
        });
        Object.assign(s, {
          phone,
          verificationId: verification.verification_id,
          code: '',
          step: 'code',
        });
      } finally {
        captcha?.reset();
      }
    });
  }

  async function confirm() {
    let expired = false;
    await run(async () => {
      try {
        s.appointment = await api.post<ConfirmedAppointment>('/appointments', {
          hold_id: s.hold!.hold_id,
          verification_id: s.verificationId,
          code: s.code.trim(),
          client: {
            full_name: s.form.name.trim(),
            phone: s.phone,
            ...(s.form.email.trim() ? { email: s.form.email.trim() } : {}),
          },
          ...(s.form.notes.trim() ? { notes: s.form.notes.trim() } : {}),
        });
        stopCountdown();
        dropCaptcha();
        s.hold = undefined;
        s.step = 'done';
      } catch (error) {
        if (error instanceof WidgetApiError && error.code === 'hold_expired') {
          expired = true;
          return;
        }
        throw error;
      }
    });
    if (expired) await backToTime('error.hold_expired');
  }

  async function cancel() {
    if (!window.confirm(t('done.cancelConfirm'))) return;
    const { id, token } = s.appointment!;
    await run(async () => {
      await api.post(`/appointments/${id}/cancel`, { token });
      s.cancelled = true;
    });
  }

  function restart() {
    show('service', {
      service: undefined,
      location: undefined,
      appointment: undefined,
      cancelled: false,
      alternatives: [],
      form: { ...s.form, notes: '' },
    });
  }

  // --- экраны ---

  const back = (onClick: () => void) =>
    h('button', { type: 'button', class: 'link', onclick: onClick }, `← ${t('back')}`);

  const header = (title: MessageKey, onBack?: () => void) =>
    h('div', { class: 'bar' }, h('h2', {}, t(title)), onBack && back(onBack));

  const price = (service: PublicService) =>
    service.price === null
      ? ''
      : new Intl.NumberFormat(locale, { style: 'currency', currency: service.currency }).format(
          Number(service.price),
        );

  function field(
    label: string,
    name: keyof typeof s.form,
    attrs: Record<string, unknown> = {},
    multiline = false,
  ) {
    const control = h(multiline ? 'textarea' : 'input', {
      value: s.form[name],
      oninput: (e: Event) => (s.form[name] = (e.target as HTMLInputElement).value),
      ...attrs,
    });
    return h('label', {}, label, control);
  }

  let countryList: { locale: Locale; items: { code: CountryCode; name: string }[] } | undefined;

  /**
   * Страны на языке формы: названия берём у Intl, в словарях их нет (§9). Список
   * упорядочен по названию, а в строке первым идёт код: в узком поле на телефоне
   * название обрезается, и код всё равно виден.
   */
  function countries() {
    if (countryList?.locale !== locale) {
      const names =
        typeof Intl.DisplayNames === 'function'
          ? new Intl.DisplayNames([locale], { type: 'region' })
          : undefined;
      const items = (Object.keys(DIAL_CODES) as CountryCode[])
        .map((code) => ({ code, name: names?.of(code) ?? code }))
        .sort((a, b) => a.name.localeCompare(b.name, locale));
      countryList = { locale, items };
    }
    return countryList.items;
  }

  const nameOf = (code: CountryCode) => countries().find((c) => c.code === code)?.name ?? code;

  /** Поиск в списке стран: по названию, коду страны (US) и телефонному коду (+374). */
  function matchCountries(query: string) {
    const text = fold(query);
    if (text === '') return countries();
    const digits = query.replace(/\D/g, '');
    return countries().filter(
      (c) =>
        (digits !== '' && DIAL_CODES[c.code].startsWith(digits)) ||
        fold(c.name).includes(text) ||
        c.code.toLowerCase().startsWith(text),
    );
  }

  /**
   * Выбор страны: кнопка с выбранной страной и выпадающий список с поиском — стран
   * больше двухсот, в обычном select их не найти. Разметка — как у combobox с
   * listbox (ARIA), поэтому список доступен с клавиатуры и в скринридере.
   */
  function countryPicker(onPick: () => void) {
    const label = (code: CountryCode) => `+${DIAL_CODES[code]} ${nameOf(code)}`;
    const name = h('span', { class: 'cc-name' }, label(s.country));
    const button = h(
      'button',
      {
        type: 'button',
        class: 'cc-button',
        'aria-label': t('details.country'),
        'aria-haspopup': 'listbox',
        'aria-expanded': 'false',
        onclick: () => (panel.hidden ? open() : close(true)),
      },
      name,
      h('span', { class: 'cc-arrow', 'aria-hidden': 'true' }),
    );
    const list = h('div', { class: 'cc-list', id: 'db-cc-list', role: 'listbox' });
    const empty = h('p', { class: 'muted cc-empty', hidden: true }, t('details.countryNone'));
    const search = h('input', {
      class: 'cc-search',
      type: 'text',
      role: 'combobox',
      autocomplete: 'off',
      spellcheck: false,
      placeholder: t('details.countrySearch'),
      'aria-label': t('details.countrySearch'),
      'aria-controls': 'db-cc-list',
      'aria-expanded': 'true',
      'aria-autocomplete': 'list',
      oninput: draw,
      onkeydown: (e: KeyboardEvent) => keys(e),
    });
    const panel = h('div', { class: 'cc-panel', hidden: true }, search, list, empty);
    const box = h('div', { class: 'cc' }, button, panel);

    let shown: { code: CountryCode; name: string }[] = [];
    let active = 0;

    function draw() {
      shown = matchCountries(search.value);
      active = Math.max(
        0,
        shown.findIndex((c) => c.code === s.country),
      );
      list.replaceChildren(
        ...shown.map((c) =>
          h(
            'div',
            {
              class: 'cc-option',
              id: `db-cc-${c.code}`,
              role: 'option',
              'aria-selected': String(c.code === s.country),
              // mousedown не даём: фокус должен остаться в поле поиска
              onmousedown: (e: Event) => e.preventDefault(),
              onclick: () => pick(c.code),
            },
            label(c.code),
          ),
        ),
      );
      empty.hidden = shown.length > 0;
      highlight();
    }

    function highlight() {
      const current = shown[active];
      for (const option of list.children) option.classList.remove('on');
      if (!current) return search.removeAttribute('aria-activedescendant');
      const option = list.children[active] as HTMLElement;
      option.classList.add('on');
      option.scrollIntoView({ block: 'nearest' });
      search.setAttribute('aria-activedescendant', option.id);
    }

    function keys(e: KeyboardEvent) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        if (shown.length === 0) return;
        active = (active + (e.key === 'ArrowDown' ? 1 : shown.length - 1)) % shown.length;
        highlight();
      } else if (e.key === 'Enter') {
        e.preventDefault(); // Enter в поиске не отправляет форму
        const current = shown[active];
        if (current) pick(current.code);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        close(true);
      }
    }

    function open() {
      panel.hidden = false;
      button.setAttribute('aria-expanded', 'true');
      search.value = '';
      draw();
      search.focus();
    }

    function close(toButton: boolean) {
      panel.hidden = true;
      button.setAttribute('aria-expanded', 'false');
      if (toButton) button.focus();
    }

    function pick(code: CountryCode) {
      s.country = code;
      name.textContent = label(code);
      close(true);
      onPick();
    }

    // Ушли из поля — список закрываем (клик мимо переводит фокус наружу)
    box.addEventListener('focusout', (e) => {
      if (!box.contains((e as FocusEvent).relatedTarget as Node | null)) close(false);
    });
    return box;
  }

  const numberFormat = () => NUMBER_FORMATS[DIAL_CODES[s.country]];

  /**
   * Маска номера выбранной страны. Текст перестраиваем целиком, поэтому курсор ставим
   * заново — после той же по счёту цифры, иначе правка в середине уводит его в конец.
   */
  function reformat(input: HTMLInputElement) {
    const text = maskNational(DIAL_CODES[s.country], input.value);
    if (text !== null && text !== input.value) {
      const caret = input.selectionStart ?? input.value.length;
      const typed = input.value.slice(0, caret).replace(/\D/g, '').length;
      input.value = text;
      let at = 0;
      for (let seen = 0; at < text.length && seen < typed; at += 1) {
        if (text[at]! >= '0' && text[at]! <= '9') seen += 1;
      }
      input.setSelectionRange(at, at);
    }
    s.form.phone = input.value;
  }

  /**
   * Телефон: код страны отдельным списком, номер — отдельным полем. Подпись связана с
   * полем номера через for/id, у списка своя (label оборачивал бы оба поля и достался
   * бы только первому).
   */
  function phoneField() {
    const number = h('input', {
      id: 'db-phone',
      type: 'tel',
      autocomplete: 'tel-national',
      inputMode: 'tel',
      required: true,
      placeholder: numberFormat(),
      value: s.form.phone,
      oninput: () => reformat(number),
    });
    // Сменилась страна — меняются и подсказка, и маска
    const picker = countryPicker(() => {
      const pattern = numberFormat();
      number.placeholder = pattern ?? '';
      if (pattern === undefined) number.value = number.value.replace(/\D/g, '');
      reformat(number);
    });
    return h(
      'div',
      { class: 'tel-field' },
      h('label', { for: 'db-phone' }, t('details.phone')),
      h('div', { class: 'tel' }, picker, number),
    );
  }

  function screen(): Child[] {
    const error = s.error && h('div', { class: 'error', role: 'alert' }, t(s.error));
    switch (s.step) {
      case 'loading':
        return [h('p', { class: 'muted' }, t('loading'))];

      case 'unavailable':
        return [h('p', {}, t('error.unavailable'))];

      case 'service': {
        const list = h('div', { class: 'list' });
        const none = h('p', { class: 'muted', hidden: true }, t('service.none'));
        const draw = (query: string) => {
          const groups = groupServices(s.services, query);
          list.replaceChildren(
            ...groups.flatMap((group) => [
              ...(group.category !== null || groups.length > 1
                ? [h('h3', { class: 'group' }, group.category ?? t('service.noCategory'))]
                : []),
              ...group.items.map((service) =>
                h(
                  'button',
                  { type: 'button', class: 'item', onclick: () => chooseService(service) },
                  h(
                    'span',
                    {},
                    service.name,
                    h('small', {}, t('service.minutes', { min: service.duration_min })),
                  ),
                  h('span', {}, price(service)),
                ),
              ),
            ]),
          );
          none.hidden = groups.length > 0;
        };
        const search = h('input', {
          type: 'search',
          class: 'search',
          autocomplete: 'off',
          spellcheck: false,
          placeholder: t('service.search'),
          'aria-label': t('service.search'),
          oninput: () => draw(search.value),
          // Enter в поиске не отправляет форму и не перезагружает страницу клиники
          onkeydown: (e: KeyboardEvent) => e.key === 'Enter' && e.preventDefault(),
        });
        draw('');
        return [
          h('h2', {}, t('service.title')),
          error,
          s.services.length > SEARCH_FROM && search,
          list,
          none,
        ];
      }

      case 'office':
        return [
          header('office.title', () => show('service')),
          error,
          h(
            'div',
            { class: 'list' },
            ...s.config!.locations.map((office) =>
              h(
                'button',
                { type: 'button', class: 'item', onclick: () => void chooseOffice(office) },
                h('span', {}, office.name, office.address && h('small', {}, office.address)),
              ),
            ),
          ),
        ];

      case 'time': {
        const today = todayIn(zone());
        const day = s.days.find((d) => d.date === s.date);
        const anyFree = s.days.some((d) => d.slots.length > 0);
        return [
          header('time.title', () =>
            s.config!.locations.length > 1 ? show('office') : show('service'),
          ),
          error,
          s.alternatives.length > 0 &&
            h(
              'div',
              { class: 'note' },
              t('time.taken'),
              h(
                'div',
                { class: 'slots', style: 'margin-top:8px' },
                ...s.alternatives.map((slot) =>
                  h(
                    'button',
                    { type: 'button', class: 'slot', onclick: () => void holdSlot(slot) },
                    formatTime(slot, zone(), locale),
                  ),
                ),
              ),
            ),
          h(
            'div',
            { class: 'days', role: 'group' },
            ...s.days.map((d) =>
              h(
                'button',
                {
                  type: 'button',
                  class: 'day',
                  disabled: d.slots.length === 0,
                  'aria-pressed': String(d.date === s.date),
                  onclick: () => show('time', { date: d.date, alternatives: [] }),
                },
                formatDay(d.date, locale),
              ),
            ),
          ),
          !anyFree
            ? h('p', { class: 'muted' }, t('time.noneAll'))
            : day && day.slots.length > 0
              ? h(
                  'div',
                  { class: 'slots' },
                  ...day.slots.map((slot) =>
                    h(
                      'button',
                      {
                        type: 'button',
                        class: 'slot',
                        disabled: s.busy,
                        onclick: () => void holdSlot(slot),
                      },
                      formatTime(slot, zone(), locale),
                    ),
                  ),
                )
              : h('p', { class: 'muted' }, t('time.none')),
          h(
            'div',
            { class: 'nav' },
            h(
              'button',
              {
                type: 'button',
                class: 'link',
                disabled: s.from <= today || s.busy,
                onclick: () => void loadDays(addDays(s.from, -WINDOW_DAYS)),
              },
              `← ${t('time.earlier')}`,
            ),
            h(
              'button',
              {
                type: 'button',
                class: 'link',
                disabled: s.busy,
                onclick: () => void loadDays(addDays(s.from, WINDOW_DAYS)),
              },
              `${t('time.later')} →`,
            ),
          ),
        ];
      }

      case 'details': {
        const reserved = h('div', { class: 'note', 'aria-live': 'polite' });
        startCountdown(reserved);
        const siteKey = s.config!.captcha?.site_key;
        if (siteKey && !captchaBox) {
          captchaBox = h('div', { class: 'captcha' });
          captcha = renderCaptcha(captchaBox, siteKey, locale);
        }
        return [
          header('details.title', () => void backToTime()),
          h(
            'p',
            {},
            h(
              'strong',
              {},
              `${formatDateOf(s.hold!.start_at, zone(), locale)}, ${formatTime(s.hold!.start_at, zone(), locale)}`,
            ),
            h('br'),
            h('span', { class: 'muted' }, `${s.service!.name} · ${s.location!.name}`),
          ),
          reserved,
          error,
          h(
            'form',
            {
              novalidate: true,
              onsubmit: (e: Event) => {
                e.preventDefault();
                void sendCode();
              },
            },
            field(t('details.name'), 'name', { autocomplete: 'name', required: true }),
            phoneField(),
            h('p', { class: 'muted' }, t('details.phoneHint')),
            field(t('details.email'), 'email', { type: 'email', autocomplete: 'email' }),
            field(t('details.notes'), 'notes', { rows: 2 }, true),
            captchaBox,
            h(
              'button',
              { type: 'submit', class: 'btn wide', disabled: s.busy },
              t('details.submit'),
            ),
          ),
        ];
      }

      case 'code': {
        const reserved = h('div', { class: 'note', 'aria-live': 'polite' });
        startCountdown(reserved);
        return [
          header('code.title', () => show('details')),
          h('p', {}, t('code.sent', { phone: s.phone })),
          reserved,
          error,
          h(
            'form',
            {
              onsubmit: (e: Event) => {
                e.preventDefault();
                void confirm();
              },
            },
            h(
              'label',
              {},
              t('code.label'),
              h('input', {
                class: 'code',
                value: s.code,
                inputMode: 'numeric',
                autocomplete: 'one-time-code',
                maxLength: 6,
                pattern: '\\d{6}',
                required: true,
                oninput: (e: Event) => (s.code = (e.target as HTMLInputElement).value),
              }),
            ),
            h('button', { type: 'submit', class: 'btn wide', disabled: s.busy }, t('code.submit')),
          ),
          h(
            'div',
            { class: 'nav' },
            h(
              'button',
              { type: 'button', class: 'link', onclick: () => show('details') },
              t('code.change'),
            ),
          ),
        ];
      }

      case 'done': {
        const a = s.appointment!;
        return [
          h(
            'div',
            { class: 'done' },
            h('div', { class: 'mark', 'aria-hidden': 'true' }, s.cancelled ? '×' : '✓'),
            h('h2', {}, s.cancelled ? t('done.cancelled') : t('done.title')),
            !s.cancelled && a.status === 'pending' && h('p', {}, t('done.pending')),
            h(
              'p',
              {},
              h(
                'strong',
                {},
                `${formatDateOf(a.start_at, a.time_zone, locale)}, ${formatTime(a.start_at, a.time_zone, locale)}`,
              ),
              h('br'),
              `${a.service.name} · ${a.location.name}`,
              a.location.address && h('br'),
              a.location.address,
              h('br'),
              t('done.with', { name: a.dentist.full_name }),
            ),
            error,
            !s.cancelled &&
              h(
                'button',
                { type: 'button', class: 'link', disabled: s.busy, onclick: () => void cancel() },
                t('done.cancel'),
              ),
            h('p', {}),
            h('button', { type: 'button', class: 'btn', onclick: restart }, t('done.again')),
          ),
        ];
      }
    }
  }

  function render() {
    stopCountdown();
    screens.replaceChildren(...screen().filter((c): c is Node | string => Boolean(c)));
  }

  let started = false;
  function open() {
    if (!started) {
      started = true;
      void start();
    }
    popup?.open();
  }

  render();
  // Окно не ходит в API, пока его не открыли: кнопки записи стоят на многих страницах
  // сайта, а нажимают их редко. Закрытое и открытое снова, оно продолжает с того же шага
  if (!popup) open();
  return { open };
}
